/**
 * Deciding what a sideways swipe on an iOS grid does. Pure, unit-tested in `e2e/unit/gridTouchAxis.spec.ts`;
 * `hooks/useTouchAxisLock.ts` uses it.
 */

export type TouchAxis = "x" | "y";

/** One point of a drag: when it happened (ms) and where the finger was (px). */
export type TrailPoint = [time: number, position: number];

/** How far the finger travels before the swipe is held to one direction (px). */
export const AXIS_THRESHOLD = 6;

/** How long a released swipe keeps coasting at its speed, when working out where it lands (ms). */
const COAST_MS = 220;

/** A finger still for longer than this before lifting has stopped: the swipe does not coast (ms). */
const REST_MS = 80;

/** The direction of a drag, or null while it is too short to tell. A tie is vertical: reading down a list is the common case. */
export const resolveAxis = (dx: number, dy: number): TouchAxis | null => {
    const across = Math.abs(dx);
    const down = Math.abs(dy);
    if (across > AXIS_THRESHOLD && across > down) return "x";
    if (down > AXIS_THRESHOLD) return "y";
    return null;
};

/** The finger's speed at `releasedAt` over a trail of points (px/ms); 0 with too few to measure, or once it has rested. */
export const releaseSpeed = (trail: readonly TrailPoint[], releasedAt: number): number => {
    if (trail.length < 2) return 0;
    const [firstTime, firstPosition] = trail[0];
    const [lastTime, lastPosition] = trail[trail.length - 1];
    if (releasedAt - lastTime > REST_MS) return 0;
    return lastTime > firstTime ? (lastPosition - firstPosition) / (lastTime - firstTime) : 0;
};

/**
 * Where a released swipe comes to rest: the column edge nearest to where it would coast to, so a column ends up
 * lined up against the pinned one. `edges` are scroll offsets. A finger moving right (`speed` > 0) scrolls back.
 */
export const snapTarget = (edges: readonly number[], scrollLeft: number, speed: number, maxScrollLeft: number): number => {
    const landing = scrollLeft - speed * COAST_MS;
    return [...edges, maxScrollLeft].reduce((best, edge) => (Math.abs(edge - landing) < Math.abs(best - landing) ? edge : best), 0);
};
